import { useVoiceRecorder } from '@euler/app/components/voice/VoiceRecordingProvider';
import { useServiceFactory } from '@euler/services/factory';
import { VoiceRecognizeOptions } from '@euler/services/voice.service';
import { useCallback, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type VoiceRecognizeCallbacks = {
  /**
   * Recognition succeeded with non-empty text. (For empty results, see
   * `onEmpty`.)
   */
  onResult: (text: string) => void;
  /**
   * Recognition succeeded but the recognizer didn't pick anything up.
   * Override the default "未识别到内容" toast.
   */
  onEmpty?: () => void;
  /** User tapped cancel — no recognition was performed. */
  onCancel?: () => void;
  /**
   * Recording or recognition failed. Override the default error toast
   * ("无法开始录音：…" for start failures, "语音识别失败：…" for stop /
   * recognize failures).
   */
  onError?: (error: unknown) => void;
};

/**
 * Combines voice recording (via the global VoiceRecordingProvider) with the
 * server-side recognition API call. Surfaces the recognized text via
 * `callbacks.onResult`; empty / error / start-failure cases default to
 * top-positioned toasts and can be overridden via `onEmpty` / `onError`.
 */
export const useVoiceRecognize = (options?: VoiceRecognizeOptions) => {
  const voiceRecorder = useVoiceRecorder();
  const { voiceService } = useServiceFactory();
  const [isRecognizing, setIsRecognizing] = useState(false);
  const safeAreaInsets = useSafeAreaInsets();
  // Push toasts below the dynamic island / status bar / notch. Recomputed
  // when the inset changes (e.g. orientation change).
  const toastTopOffset = safeAreaInsets.top + 56;

  // Read latest options/offset at recognize-time so callers don't have to
  // memoize the options object.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const toastTopRef = useRef(toastTopOffset);
  toastTopRef.current = toastTopOffset;

  const start = useCallback(
    (callbacks: VoiceRecognizeCallbacks) => {
      const showToast = (message: string) => {
        Toast.show(message, {
          duration: Toast.durations.SHORT,
          position: toastTopRef.current,
        });
      };
      const handleError = (e: unknown, prefix: string) => {
        if (callbacks.onError) {
          callbacks.onError(e);
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        console.warn(prefix, e);
        showToast(`${prefix}${message}`);
      };

      voiceRecorder.start({
        onComplete: async recording => {
          setIsRecognizing(true);
          try {
            const result = await voiceService.recognize({
              ...recording,
              ...optionsRef.current,
            });
            const text = result.text?.trim() ?? '';
            if (text) {
              callbacks.onResult(text);
            } else if (callbacks.onEmpty) {
              callbacks.onEmpty();
            } else {
              showToast('未识别到内容');
            }
          } catch (e) {
            handleError(e, '语音识别失败：');
          } finally {
            setIsRecognizing(false);
          }
        },
        onCancel: callbacks.onCancel,
        onError: e => handleError(e, '无法开始录音：'),
      });
    },
    [voiceRecorder, voiceService],
  );

  return {
    isRecording: voiceRecorder.isRecording,
    isRecognizing,
    start,
  };
};
